import { CSSProperties } from 'react';
import {
    dateFnsLocalizer,
    EventProps,
    Formats,
    View,
    Views,
} from 'react-big-calendar';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import format from 'date-fns/format';
import getDay from 'date-fns/getDay';
import parse from 'date-fns/parse';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import startOfWeek from 'date-fns/startOfWeek';
import enUS from 'date-fns/locale/en-US';
import nb from 'date-fns/locale/nb';
import { useTranslation } from 'react-i18next';

import { LoopIcon } from 'src/components/Icons/icons';
import {
    EventType,
    EventStatus,
    getEventIcon,
    getEventTypeColor,
    getMunduEventType,
    MunduEvent,
} from 'src/domain/event';
import { EventIconGraphic } from './EventIconGraphic';

const locales = {
    'en-US': enUS,
    nb,
};

export type CalendarMode = 'calendar' | 'myEvents' | 'readView';
export type MyEventsFilter = 'department' | 'createdByMe';

export type CalendarSlotEvent = {
    id: string;
    title: string;
    start: Date;
    end: Date;
    allDay?: boolean;
    isCreatedByCurrentUser: boolean;
    isCurrentBoundary?: boolean;
    currentBoundaryLabel?: string;
    resource: MunduEvent;
};

export const startOfCalendarWeek = (date: Date) =>
    startOfWeek(date, { weekStartsOn: 1 });

export const endOfCalendarWeek = (date: Date) =>
    endOfWeek(date, { weekStartsOn: 1 });

export const formatWeekMonthLabel = (date: Date, locale: typeof nb) => {
    const weekStart = startOfCalendarWeek(date);
    const weekEnd = endOfCalendarWeek(date);
    const startMonth = format(weekStart, 'LLLL', { locale });

    if (format(weekStart, 'yyyy-MM') === format(weekEnd, 'yyyy-MM')) {
        return startMonth;
    }

    const endMonth = format(weekEnd, 'LLLL', { locale });

    if (format(weekStart, 'yyyy') === format(weekEnd, 'yyyy')) {
        return `${startMonth} – ${endMonth}`;
    }

    return `${format(weekStart, 'LLLL yyyy', {
        locale,
    })} – ${format(weekEnd, 'LLLL yyyy', { locale })}`;
};

export const calendarLocalizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: startOfCalendarWeek,
    getDay,
    locales,
});

export const formatCalendarTime = (date: Date) => format(date, 'HH:mm');

const formatCalendarTimeRange = ({ start, end }: { start: Date; end: Date }) =>
    `${formatCalendarTime(start)} – ${formatCalendarTime(end)}`;

export const calendarScrollToTime = new Date(1970, 0, 1, 8, 0, 0);

export const calendarFormats: Formats = {
    timeGutterFormat: formatCalendarTime,
    agendaTimeFormat: formatCalendarTime,
    agendaTimeRangeFormat: formatCalendarTimeRange,
    eventTimeRangeFormat: formatCalendarTimeRange,
    eventTimeRangeStartFormat: ({ start }) => `${formatCalendarTime(start)} –`,
    eventTimeRangeEndFormat: ({ end }) => `– ${formatCalendarTime(end)}`,
    selectRangeFormat: formatCalendarTimeRange,
};

export const calendarViews: View[] = [
    Views.MONTH,
    Views.WEEK,
    Views.DAY,
    Views.AGENDA,
];

export const getCalendarEventProps = (event: CalendarSlotEvent) => {
    const classNames = [];
    const eventType = getMunduEventType(event.resource);
    const isCancelled = event.resource.status === EventStatus.cancelled;
    const color = getEventTypeColor(eventType);
    const style = {
        '--mundu-event-color': color.background,
        '--mundu-event-text-color': color.foreground,
        ...(event.currentBoundaryLabel
            ? {
                  '--mundu-current-boundary-label': `"${event.currentBoundaryLabel}"`,
              }
            : {}),
    } as CSSProperties;

    if (isCancelled) {
        classNames.push('mundu-calendar-event-cancelled');
    } else {
        if (event.isCreatedByCurrentUser) {
            classNames.push('mundu-calendar-event-created-by-me');
        }
        if (eventType === EventType.internal) {
            classNames.push('mundu-calendar-event-internal');
        }
        if (event.end.getTime() <= Date.now()) {
            classNames.push('mundu-calendar-event-past');
        }
    }
    if (event.isCurrentBoundary) {
        classNames.push('mundu-calendar-current-boundary');
    }
    return {
        className: classNames.join(' '),
        style,
    };
};

export const getListFocusDateKey = (
    events: CalendarSlotEvent[],
    monthDate: Date,
): string | undefined => {
    if (!events.length) return undefined;

    const monthStart = startOfMonth(monthDate).getTime();
    const monthEnd = endOfMonth(monthDate).getTime();
    const today = startOfDay(new Date()).getTime();
    const focusStart =
        today >= monthStart && today <= monthEnd ? today : monthStart;
    let nextEventDay: number | undefined;
    let previousEventDay: number | undefined;

    events.forEach(event => {
        const eventStart = Math.max(
            startOfDay(event.start).getTime(),
            monthStart,
        );
        const eventEnd = Math.min(startOfDay(event.end).getTime(), monthEnd);

        if (eventEnd < monthStart || eventStart > monthEnd) return;

        if (eventEnd >= focusStart) {
            const candidate = Math.max(eventStart, focusStart);
            nextEventDay =
                nextEventDay === undefined
                    ? candidate
                    : Math.min(nextEventDay, candidate);
            return;
        }

        previousEventDay =
            previousEventDay === undefined
                ? eventEnd
                : Math.max(previousEventDay, eventEnd);
    });

    const focusDay = nextEventDay ?? previousEventDay;
    return focusDay === undefined
        ? undefined
        : format(new Date(focusDay), 'yyyy-MM-dd');
};

export const CalendarAgendaDate = ({
    day,
    label,
}: {
    day: Date;
    label: string;
}) => <span data-calendar-date={format(day, 'yyyy-MM-dd')}>{label}</span>;

export const CalendarEventContent = ({
    event,
    title,
}: EventProps<CalendarSlotEvent>) => {
    const { t } = useTranslation();
    const eventIcon = getEventIcon(event.resource.icon);

    return (
        <span className="mundu-calendar-event-content">
            <span
                className={`mundu-calendar-event-marker${
                    eventIcon ? ' mundu-calendar-event-marker-has-icon' : ''
                }`}
                aria-hidden="true"
            >
                {eventIcon ? (
                    <EventIconGraphic
                        icon={eventIcon.key}
                        className="h-full w-full"
                    />
                ) : null}
            </span>
            <span className="mundu-calendar-event-title">
                {title}
                {event.resource.status === EventStatus.cancelled
                    ? ` – ${t('modules.calendar.form.cancelledLabel')}`
                    : ''}
            </span>
            {event.resource.recurrence ? (
                <span
                    className="mundu-calendar-recurrence-icon"
                    role="img"
                    aria-label={t('modules.calendar.views.recurringEvent')}
                    title={t('modules.calendar.views.recurringEvent')}
                >
                    <LoopIcon />
                </span>
            ) : null}
        </span>
    );
};

// The print view includes resident and relatives events for a week; other list views cover a month.
export const getActiveView = (calendarMode: CalendarMode, view: View): View => {
    if (calendarMode === 'calendar') return view;

    return calendarMode === 'readView' ? Views.WEEK : Views.AGENDA;
};

export const getVisibleRange = (
    date: Date,
    view: View,
): { from: Date; to: Date } => {
    switch (view) {
        case Views.DAY:
            return { from: startOfDay(date), to: endOfDay(date) };
        case Views.WEEK:
            return {
                from: startOfCalendarWeek(date),
                to: endOfCalendarWeek(date),
            };
        case Views.AGENDA:
            return { from: startOfMonth(date), to: endOfMonth(date) };
        case Views.MONTH:
        default:
            return {
                from: startOfCalendarWeek(startOfMonth(date)),
                to: endOfCalendarWeek(endOfMonth(date)),
            };
    }
};
